/**-----------------------------------------------------------------------------
 *                                    E2RH
 * -----------------------------------------------------------------------------
 *
 *  filename: login.js
 *
 *  author: Thomas CLERET (thomas.cleret@proton.me)
 *
 *  creation: 20/9/2025 - 18:44
 *
 *  description:
 *
 *  Copyright (c) 2025 E2RH
 * -----------------------------------------------------------------------------
 */

import * as React from "react";
import PropTypes from "prop-types";
import { toast } from "react-toastify";
import * as utils from "../utils/utils.tsx";
import TextField from "@mui/material/TextField";

/**
 * Login component, displays the login menu
 *
 * @param {callback} onSetActionsList callback for the setActionList state
 * @param {callback} onSetCurrentAction callback for the setCurrentAction state
 * @param {callback} onSetIsLoggedIn callback for the setIsLoggedIn state
 */
export default function Login({
   onSetActionsList,
   onSetAccountActionsList,
   onSetCurrentAction,
   onSetIsLoggedIn,
   onSetDomainsList,
}) {
   const usernameInputId = "username-input";
   const passwordInputId = "password-input";

   const [showPassword, setShowPassword] = React.useState(false);

   // Create callbacks to set the App state
   const setActionsList = React.useCallback(
      (event) => {
         onSetActionsList(event);
      },
      [onSetActionsList]
   );
   const setAccountActionsList = React.useCallback(
      (event) => {
         onSetAccountActionsList(event);
      },
      [onSetAccountActionsList]
   );
   const setCurrentAction = React.useCallback(
      (event) => {
         onSetCurrentAction(event);
      },
      [onSetCurrentAction]
   );
   const setIsLoggedIn = React.useCallback(
      (event) => {
         onSetIsLoggedIn(event);
      },
      [onSetIsLoggedIn]
   );
   const setDomainsList = React.useCallback(
      (event) => {
         onSetDomainsList(event);
      },
      [onSetDomainsList]
   );

   /**
    * Send credentials to the back-end for validation
    */
   async function handleLogin(event) {
      if (event != null) {
         event.preventDefault();
      }
      let username = "";
      let password = "";
      if (document.getElementById(usernameInputId) != null && document.getElementById(passwordInputId) != null) {
         username = document.getElementById(usernameInputId).value;
         password = document.getElementById(passwordInputId).value;
      }

      let token = null;

      // Get connexion token if existing
      if ((token = localStorage.getItem("token")) != undefined) {
         username = null;
         password = null;
      } else {
         token = null;
      }
      let response = null;
      try {
         response = await utils.apiClient.post("/login", { username: username, password: password, token: token });

         // Handle login reply
         localStorage.setItem("token", response.data.token);

         // Get the actions from login reply, sort them by action / accountAction
         const actionsList = [utils.MenuAction.Dashboard];
         const accountActionsList = [];
         for (const action in response.data.actions) {
            const actionName = response.data.actions[action];
            if (Object.values(utils.MenuAction).includes(actionName.split("/")[0])) {
               actionsList.push(actionName);
            }
            if (Object.values(utils.AccountAction).includes(actionName)) {
               accountActionsList.push(actionName);
            }
         }
         accountActionsList.push(utils.AccountAction.SetPasswd);
         accountActionsList.push(utils.AccountAction.Logout);

         // Store data received from the login request
         setActionsList(actionsList);
         setAccountActionsList(accountActionsList);
         setDomainsList(response.data.domains);

         // Choose which action to display
         let actionToDisplay = utils.MenuAction.Dashboard;
         let displayIfAvailable = utils.MenuAction.OpAgrume;
         let last_action = localStorage.getItem("last_action");
         if (last_action != null) {
            displayIfAvailable = last_action;
         }
         if (response.data.actions) {
            if (response.data.actions.includes(displayIfAvailable)) {
               actionToDisplay = displayIfAvailable;
            }
         }
         setCurrentAction(actionToDisplay);
         setIsLoggedIn(true);
      } catch (error) {
         toast.error(error);
         localStorage.removeItem("token");
      }
   }

   // Reconnect if page reloaded
   if (localStorage.getItem("token")) {
      handleLogin(null);
   }

   return (
      <div className="center-page">
         <div className="login_wrapper">
            <div className="login_title">Connexion</div>
            <form className="login_form" onSubmit={(event) => handleLogin(event)}>
               <div className="w100">
                  <TextField id={usernameInputId} label="Adresse e-mail" fullWidth />
               </div>
               {utils.passwordField("Mot de passe", passwordInputId, showPassword, setShowPassword)}
               <div className="login_field w100">
                  <button type="submit">Se connecter</button>
               </div>
            </form>
         </div>
      </div>
   );
}

Login.propTypes = {
   onSetActionsList: PropTypes.func,
   onSetAccountActionsList: PropTypes.func,
   onSetCurrentAction: PropTypes.func,
   onSetIsLoggedIn: PropTypes.func,
   onSetDomainsList: PropTypes.func,
};
